import { AnchorLink } from "gatsby-plugin-anchor-links";
import gsap from "gsap";
import React, { useRef, useLayoutEffect, ReactElement } from "react";
import { Box, Flex, Heading, Text } from "rebass";
import styled from "styled-components";
import Arrow from "../icons/Arrow";
import CompanyLink from "./CompanyLink";

interface IProps {
  title: string;
  titleRow?: string | undefined;
  titleClassName?: string | undefined;
  subtitle?: string | undefined;
  shortDesc?: ReactElement | null;
  role?: ReactElement | null;
  roleDesc?: ReactElement | null;
  company?: string | null;
  disciplines?: ReactElement | null;
  challenge?: ReactElement | null;
  year?: string | undefined;
  img?: string | undefined;
  projectType?: string | undefined;
  productType?: string | undefined;
  projectBg?: string | undefined;
  projectText?: string | undefined;
  component?: JSX.Element | undefined;
}

const Hero = (props: IProps) => {
  let pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const pageUrl = pathname + "#project-content";
  const sectionUrl = pathname + "#my-contribution";
  const revealRefsHero = useRef([null]);
  revealRefsHero.current = [];
  useLayoutEffect(() => {
    revealRefsHero.current.forEach((el) => {
      gsap.fromTo(
        el,
        {
          autoAlpha: 0,
        },
        {
          duration: 1,
          autoAlpha: 1,
          delay: 0.1,
          ease: "Power4.easeOut",
          stagger: {
            each: 0.1,
            from: "center",
            grid: "auto",
            ease: "Power2.inOut",
          },
        }
      );
    });
  }, []);

  const addToRefs = (el: any) => {
    if (el && !revealRefsHero.current.includes(el)) {
      revealRefsHero.current.push(el);
    }
  };

  return (
    <StyledSection>
      <Box 
        mb={[4,5]}
        sx={{ 
            ...(props.projectBg && { backgroundColor: props.projectBg }) 
          }}
           
        id="hero-section"
      >  
        <Flex flexWrap="wrap" justifyContent="center" variant="containerHero">
          <Box
            width={[1 / 1]}
            py={["2.4rem", 4, 5, 5, 6]}
            mt={[6,6,6,5]}
            sx={{
              position: "relative",
              //...(props.projectBg && { backgroundColor: props.projectBg }),
              ...(props.projectText && { color: props.projectText }),
            }}
          >
            <Flex flexWrap="wrap">
              <Box
                width={[1 / 1, 1 / 1, 8 / 12]}
                variant="containerBigTitle"
                mb={[0]}
                //pl={[4, 4, 5, 5, 6]}
              >
                {props.projectType || props.year ? (
                  <Box sx={{ display: "flex" }}>
                    {props.projectType && (
                      <Text
                        as="p"
                        className="label"
                        variant="projectLabel"
                        mb={[2, 3, 4]}
                        sx={{
                          alignSelf: "center",
                          ...(props.projectBg && { color: props.projectBg }),
                          ...(props.projectText && {
                            backgroundColor: props.projectText,
                          }),
                          ...(props.projectText && {
                            "::after": {
                              borderLeftColor: props.projectText,
                              borderTopColor: props.projectText,
                            },
                          }),
                        }}
                      >
                        {props.projectType}
                      </Text>
                    )}
                    {props.year && (
                      <Text as="p" mb={[2, 3, 4]} ml={[3]}>
                        <strong>{props.year}</strong>
                      </Text>
                    )}
                  </Box>
                ) : null}
                <Heading
                  as="h2"
                  className={"projectTitle " + props.titleClassName}
                  variant="bigTitle"
                  mt={[2]}
                  sx={{
                    ...(props.projectText && { color: props.projectText }),
                  }}
                >
                  <span ref={addToRefs}>{props.title}</span>
                  <span ref={addToRefs}>{props.titleRow}</span>
                </Heading>
              </Box>
              {/* 3D Model */}
              <Box
                width={[1 / 1, 1 / 1, 4 / 12]}
                mt={["-22vw", "-22vw", "0"]}
                variant="containerBigTitleImg"
                sx={{ position: "relative" }}
              >
                {props.component !== undefined && <>{props.component}</>}
              </Box>
            </Flex>
            {props.subtitle && (
              <Box
                width={[1 / 1, 1 / 1, 8 / 12]}
                variant="containerBigTitle"
                //px={[4, 4, 5, 5, 6]}
              >
                <Text
                  as="p"
                  my={[1, 2, 4, 4]}
                  variant="paragraphXL"
                  fontWeight={"medium"}
                  color={"inherit"}
                  maxWidth="50ch"
                >
                  {props.subtitle}
                </Text>
              </Box>
            )}

            {props.productType ? (
              <Box
                width={[1 / 1, 1 / 1, 8 / 12]}
                textAlign="left"
                variant="containerBigTitle"
                //px={[4, 4, 5, 5, 6]}
                sx={{ position: "relative", zIndex: "99" }}
              >
                <Text
                  as="p"
                  py={[3, 4]}
                  pb={[3, 4, 0]}
                  sx={{
                    borderWidth: "1px 0 0",
                    borderStyle: "solid",
                    borderColor: "borderColorQuiet",
                  }}
                >
                  <strong>{props.productType}</strong>
                </Text>
              </Box>
            ) : null}

            <Box
              width={[1 / 2]}
              textAlign="center"
              sx={{
                position: "absolute",
                zIndex: "99",
                left: "25%",
                translateY: "-50%",
                bottom: [0, 0, 4],
              }}
            >
              <StyledAnchorLink to={sectionUrl} title="My Contribution">
                {/* <span>My contribution</span> */}
                <Arrow />
              </StyledAnchorLink>
            </Box>
          </Box>
        </Flex>
      </Box>
      <Flex flexWrap="wrap" justifyContent="center" variant="containerHero">
        {/* Summary */}
        <Box variant="containerTxt" id="my-contribution">
          {props.shortDesc && (
            <Box>
              <Heading as="h6" variant="catHeading">
                Long story short
              </Heading>
              <Text as="p" variant="paragraph" ref={addToRefs} mb={[3]}>
                {props.shortDesc}
              </Text>
            </Box>
          )}
          <Flex flexWrap="wrap">
            {/* My Role */}
            <Box width={[1 / 1, 1 / 1, 2 / 5]}>
              <Heading as="h6" variant="catHeading">
                My role
              </Heading>
              <Text as="p" fontWeight={"bold"} my={[2, 3]}>
                {props.role}
              </Text>
            </Box>

            {/* Company */}
            {props.company && (
              <Box width={[1 / 1, 1 / 1, 3 / 5]}>
                <CompanyLink companyName={props.company} />
              </Box>
            )}
          </Flex>
          {/* My Role Description */}
          <Text as="p" variant="paragraph" ref={addToRefs} mt={[4]}>
            {props.roleDesc}
          </Text>

          {/* Disciplines */}
          <Heading as="h6" variant="catHeading">
            Disciplines involved (A-Z)
          </Heading>
          <Text as="p" ref={addToRefs} mb={[3, 4]} fontWeight={"bold"}>
            {props.disciplines}
          </Text>

          {/* Challenge */}
          <Heading as="h6" variant="catHeading">
            The Challenge
          </Heading>
          <Text as="p" variant="paragraph" ref={addToRefs}>
            {props.challenge}
          </Text>
        </Box>
        <Box width={[1 / 1]} textAlign="center">
          <StyledAnchorLink to={pageUrl} title="Project content">
            <span>Deep dive</span>
            <Arrow />
          </StyledAnchorLink>
        </Box>
      </Flex>
      {/* Image */}
      {props.img ? (
        <Box
          order={[1, 2]}
          width={[1 / 1]}
          sx={{ position: "relative" }}
          ref={addToRefs}
          mb={[5, 6]}
        >
          <img src={props.img} alt={props.title} />
        </Box>
      ) : null}
    </StyledSection>
  );
};

const StyledSection = styled.section`
  color: inherit;
  width: 100vw;
  justify-content: center;
  display: flex;
  flex-direction: column;
  & .projectTitle {
    padding-bottom: 0;
    margin-bottom: 0;
    & > span {
      display: block;
    }
  }
`;

const StyledAnchorLink = styled(AnchorLink)`
  position: relative;
  z-index: 99;
  & > span {
    display: block;
    opacity: 0;
    transition: opacity 0.1s ease-in-out;
    padding: 0.8rem;
  }
  ,
  &:hover span {
    opacity: 1;
  }
  ,
  & > svg,
  & > div > svg {
    width: 2.4rem;
    height: auto;
    transform: rotate(90deg);
  }
  @media only screen and (min-width: 64em) {
    & > svg,
    & > div > svg {
      width: 3.2rem;
    }
  }
`;

export default Hero;
